import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { AppContainer } from 'react-hot-loader';
import { initializeIcons } from 'office-ui-fabric-react/lib/Icons';
import { log } from 'util';
import * as moment from 'moment';
import { clientLog, serverLog, apiLink, apiGetAccount, apiLogClientError, LSKEY_FORCEINTERACTIVELOGIN } from './util';
import { BrowserAuthError, createNestablePublicClientApplication } from "@azure/msal-browser";
import { createLocalUrl } from "./util";
import { initTheme, LSKEY_THEMEMODE } from './theme';
import {
    StartupErrorCode,
    buildHelpLink,
    buildStartupDiagnostics,
    describeOfficeError,
    formatStartupDiagnostics,
    isTransientOfficeError,
    serializeError,
} from './startupDiagnostics';
import { RETRY_DELAYS_MS } from './emailTypeService';
import { decodeJwtExpiration, describeTokenExpiry, isIdTokenExpiredOrExpiring } from './idToken';

import App from './components/App';
import Debugger from './components/Debugger';

import 'office-ui-fabric-react/dist/css/fabric.min.css';
import './styles.less';

const title = 'MailMerge365';

let isOfficeInitialized = false;
let initError = null;
let initEx = null;
// Deep link into the help article, anchored at the current error code
let initHelpLink = null;
// Set while a failed step is being repeated automatically, so the user sees
// progress instead of an error that is about to resolve itself
let initRetryMessage = null;
// The step to repeat when the user presses Try again, null when repeating
// cannot help (a finished setup, a wrong account)
let initRetryAction: (() => void) = null;
let apiLinked = false;
let initMustSetup = false;
let exchangeItemID = null;
let userIdentityToken = null;
// Which acquisition path produced userIdentityToken (silent, popup, dialog).
// A rejected token in a support report says nothing without this.
let userIdentityTokenSource = null;
let userAccessToken = null;

// Simple client-side loop breaker / throttle for auth attempts
let authInProgress = false;
let authAttemptCount = 0;
let lastAuthAttemptAt = 0;
const AUTH_WINDOW_MS = 60 * 1000; // 1 minute
const MAX_AUTH_ATTEMPTS_IN_WINDOW = 2;

// The Office Dialog API runs its own MSAL instance in a separate webview, so
// it is the only path that does not go through the Outlook host's nested app
// auth broker. When the broker is serving a stale identity token it is the
// only path that can still recover, and it always needs the user to click, so
// it cannot spin on its own. The recovery attempt therefore gets one try per
// episode of its own, instead of sharing a budget the automatic re-auth retry
// has already spent.
let forceFreshDialogUsed = false;
// Set when sign-in fell back to the dialog for an unrelated reason (a blocked
// popup). Diagnostics only: it must not spend the recovery attempt above.
let dialogUsedDuringSignIn = false;

function tooManyAuthAttemptsNow(): boolean {
    const now = Date.now();
    if (now - lastAuthAttemptAt > AUTH_WINDOW_MS) {
        authAttemptCount = 0;
    }
    return authAttemptCount >= MAX_AUTH_ATTEMPTS_IN_WINDOW;
}

function recordAuthAttempt() {
    const now = Date.now();
    if (now - lastAuthAttemptAt > AUTH_WINDOW_MS) {
        authAttemptCount = 0;
    }
    authAttemptCount++;
    lastAuthAttemptAt = now;
}

function breakAuthLoop(message: string, extra?: any) {
    // Read before the reset below, which drops the token
    const attempt = describeTokenAttempt();

    // Best effort to clear all local state when loop detected
    clearAllLocalState();

    failInit(StartupErrorCode.AUTH_LOOP, "auth-loop", message, {
        attempts: authAttemptCount,
        maxAttempts: MAX_AUTH_ATTEMPTS_IN_WINDOW,
        authWindowMs: AUTH_WINDOW_MS,
        // Which path handed over the rejected token, and whether the client
        // could already see it was expired. Without these an MM-10 report
        // cannot distinguish a stale host broker from a real sign-in failure.
        ...attempt,
        linkResult: extra,
    }, onReset);
}

function getUrlParameter(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    var results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
        return match[2];
    }
    return null;
}

function setCookie(cname, cvalue) {
    var d = new Date();
    d.setTime(d.getTime() + (30 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";secure=true;path=/;SameSite=Lax;";
}

function clearCookies() {
    try {
        const cookies = document.cookie ? document.cookie.split(';') : [];
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            name = name.trim();
            if (name.length > 0) {
                // Expire at root path
                document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; secure=true;";
                // Also try current path
                document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; secure=true;";
            }
        }
    } catch { }
}

function shouldForceInteractiveLogin(): boolean {
    try { return localStorage.getItem(LSKEY_FORCEINTERACTIVELOGIN) === "1"; } catch { return false; }
}

function clearForceInteractiveLogin() {
    try { localStorage.removeItem(LSKEY_FORCEINTERACTIVELOGIN); } catch { }
}

function clearLocalStores() {
    // Keep the theme preference and the force-interactive-login flag across resets
    let themeMode = null;
    let forceInteractiveLogin = null;
    try { themeMode = localStorage.getItem(LSKEY_THEMEMODE); } catch { }
    try { forceInteractiveLogin = localStorage.getItem(LSKEY_FORCEINTERACTIVELOGIN); } catch { }

    try { sessionStorage.clear(); } catch { }
    try { localStorage.clear(); } catch { }

    try { if (themeMode) localStorage.setItem(LSKEY_THEMEMODE, themeMode); } catch { }
    try { if (forceInteractiveLogin) localStorage.setItem(LSKEY_FORCEINTERACTIVELOGIN, forceInteractiveLogin); } catch { }

    // Remove common MSAL stuck keys defensively
    try {
        const removeMsalLike = (storage: Storage) => {
            const keys: string[] = [];
            for (let i = 0; i < storage.length; i++) {
                const k = storage.key(i);
                if (!k) continue;
                if (k.startsWith('msal.') || k.indexOf('interaction') >= 0) {
                    keys.push(k);
                }
            }
            keys.forEach(k => storage.removeItem(k));
        };
        removeMsalLike(sessionStorage);
        removeMsalLike(localStorage);
    } catch { }
}

function clearRoamingSettings() {
    try {
        // Remove our known key to let auto sign-in re-enable on fresh start
        const LSKEY_AUTOSIGNINDISABLED = 'mm365-autosignindisabled';
        Office.context.roamingSettings.remove(LSKEY_AUTOSIGNINDISABLED);
        Office.context.roamingSettings.saveAsync(() => { });
    } catch { }
}

function clearAllLocalState() {
    clearCookies();
    clearLocalStores();
    clearRoamingSettings();
}

const onReset = () => {
    clientLog("Reseting")
    initEx = null;
    initError = null;
    initHelpLink = null;
    initRetryMessage = null;
    initRetryAction = null;
    initMustSetup = false;
    apiLinked = false;
    exchangeItemID = null;
    userIdentityToken = null;
    userIdentityTokenSource = null;
    userAccessToken = null;
    authAttemptCount = 0;
    lastAuthAttemptAt = 0;
    authInProgress = false;
    // Both survive an episode otherwise, and a reset that kept them would skip
    // the automatic re-auth and the dialog, the two steps that can recover
    retriedAuth = false;
    forceFreshDialogUsed = false;
    dialogUsedDuringSignIn = false;

    // An explicit reset starts a new episode, so let it report again
    Object.keys(reportedStartupCodes).forEach(code => delete reportedStartupCodes[code]);

    clearAllLocalState();
    
    render(App);
    onSignIn();
}

const render = (Component) => {
    ReactDOM.render(
        <AppContainer>
            <Component
                onReset={onReset}
                apiLinked={apiLinked}
                title={title}
                itemID={exchangeItemID}
                userIdentityToken={userIdentityToken}
                isOfficeInitialized={isOfficeInitialized}
                initError={initError}
                initEx={initEx}
                initHelpLink={initHelpLink}
                initRetryMessage={initRetryMessage}
                onInitRetry={buildInitRetryHandler()}
                initMustSetup={initMustSetup}
            />
        </AppContainer>,
        document.getElementById('container')
    );
};

const defaultInitErrorMessage = "There was an error starting the plugin. Client could not get token.";
const saveItemErrorMessage = "There was an error starting the plugin. Outlook could not save the draft message.";

// One report per code per session: a failure that repeats (item switch, poll)
// must not turn into a log flood
const reportedStartupCodes: { [code: string]: boolean } = {};

/**
 * Records a startup failure: a stable code in the user-facing message, the
 * full diagnostics in the technical-details box, the same block in the
 * browser console, and a best-effort copy on the server.
 *
 * Does not render, so a caller that may still recover (re-auth via the
 * dialog) does not flash an error at the user.
 */
const setInitFailure = (code: string, phase: string, message: string, detail?: any, retryAction?: () => void) => {
    const diagnostics = buildStartupDiagnostics(code, phase, detail);
    const formatted = formatStartupDiagnostics(diagnostics);

    initError = `${message} (Error code ${code}.)`;
    initEx = formatted;
    initHelpLink = buildHelpLink(code);
    initRetryMessage = null;
    initRetryAction = retryAction || null;

    clientLog(`Startup failed: ${formatted}`);

    try {
        if (!reportedStartupCodes[code]) {
            reportedStartupCodes[code] = true;
            // Best effort only: reporting a failure must never cause one
            apiLogClientError(code, phase, formatted).catch(() => { });
        }
    } catch { }
}

const failInit = (code: string, phase: string, message: string, detail?: any, retryAction?: () => void) => {
    setInitFailure(code, phase, message, detail, retryAction);
    render(App);
}

/**
 * Wraps the recorded retry action so pressing Try again clears the error
 * first. Returns null when the failure is not one repeating can fix, and the
 * button is then not offered at all rather than sending the user in a circle.
 */
const buildInitRetryHandler = () => {
    const action = initRetryAction;
    if (!action) {
        return null;
    }
    return () => {
        clientLog("User pressed Try again");
        initError = null;
        initEx = null;
        initHelpLink = null;
        initRetryAction = null;
        initRetryMessage = "Trying again...";
        render(App);
        action();
    };
}

const handleErrors = (method: string, result: Office.AsyncResult<any>, action: () => void, extraDetail?: any, retryAction?: () => void) => {
    if (result.status != Office.AsyncResultStatus.Succeeded) {
        // The host error says why (item not in compose, mailbox offline,
        // 5100 "previous call in progress", ...); it used to be discarded
        failInit(StartupErrorCode.SAVE_ITEM, method, saveItemErrorMessage, {
            ...describeOfficeError(result),
            ...extraDetail,
        }, retryAction);
    }
    else {
        try {
            clientLog(`${method}: OK`);
            action();
        }
        catch (ex) {
            failInit(StartupErrorCode.AFTER_SAVE, method, defaultInitErrorMessage, serializeError(ex));
        }
    }
}

let pca = undefined;
const onSignIn = async (forceTokenRefresh: boolean = false) => {

    if (authInProgress) {
        clientLog("Auth already in progress; ignoring call");
        return;
    }
    if (tooManyAuthAttemptsNow()) {
        clientLog("Too many auth attempts; breaking loop.");
        breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.");
        return;
    }

    authInProgress = true;
    recordAuthAttempt();

    // Tracks how far the sequence got, so the catch-all below reports the
    // step that failed instead of just "onSignIn"
    let phase = "start";

    try {

        userIdentityToken = null;

        // A previous attempt's failure must not leak into this one: a
        // successful retry would still render the old error, and a new
        // failure would be reported under the old code
        initError = null;
        initEx = null;
        initHelpLink = null;
        initRetryMessage = null;
        initRetryAction = null;

        clientLog("Starting")

        render(App);

        let idToken = null;
        // Which acquisition path produced idToken, carried into the
        // diagnostics so a rejected token can be traced to its source
        let tokenSource = null;
        // Paths that returned a token that had already expired. A host broker
        // that keeps doing this looks identical to "no token at all" in the
        // logs otherwise.
        const staleTokenPaths: string[] = [];

        phase = "msal-init";

        // Initialize the public client application
        pca = await createNestablePublicClientApplication({
            auth: {
                clientId: "d87015f3-71a7-42c3-80cb-5e0f636703ae",
                authority: "https://login.microsoftonline.com/organizations"
            },

        });


        const tokenRequest = {
            scopes: ["openid", "profile", "User.Read"], // copied in fallback
        };

        // After an explicit sign-out, skip silent auth and show the account
        // picker so the user can sign in with a different account
        const forceInteractive = shouldForceInteractiveLogin();

        if (forceInteractive) {
            clientLog("[MSAL] User signed out previously, forcing interactive login.");
        } else {
            phase = "acquire-silent";
            try {
                clientLog("[MSAL] Trying to acquire token silently...");
                let userAccount = await pca.acquireTokenSilent(forceTokenRefresh ? { ...tokenRequest, forceRefresh: true } : tokenRequest);

                if (!forceTokenRefresh && isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    clientLog("[MSAL] Cached ID token is expired or about to expire, retrying with forceRefresh.");
                    forceTokenRefresh = true;
                    userAccount = await pca.acquireTokenSilent({ ...tokenRequest, forceRefresh: true });
                }

                if (isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    clientLog("[MSAL] ID token still expired after refresh, falling back to interactive login.");
                    staleTokenPaths.push("silent");
                } else {
                    clientLog("[MSAL] Acquired token silently.");
                    idToken = userAccount.idToken;
                    tokenSource = forceTokenRefresh ? "silent-force-refresh" : "silent";
                }
            } catch (error) {
                clientLog(`Unable to acquire token silently: ${JSON.stringify(error)}` /* this is not a client error */);
            }
        }

        if (!idToken) {
            // Acquire token silent failure. Send an interactive request via popup.
            phase = "acquire-popup";

            // Kept for the dialog's diagnostics, and to decide whether opening
            // the dialog is worth it at all
            let popupError = null;
            // Set when the popup failed for a reason the dialog cannot fix:
            // consent, conditional access or a rejected account
            let popupFailedForGood = false;

            try {
                clientLog("[MSAL] Trying to acquire token interactively...");
                let userAccount = await pca.acquireTokenPopup(forceInteractive ? { ...tokenRequest, prompt: "select_account" } : tokenRequest);

                // Under nested app auth the Outlook host can satisfy an
                // interactive request from its own broker cache without ever
                // showing a window, so this can hand back the same expired ID
                // token the silent path just rejected. Asking for the account
                // picker forces the request past that cache.
                if (isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    clientLog("[MSAL] Interactive login returned an expired ID token, retrying with select_account.");
                    tokenSource = "popup-select-account";
                    userAccount = await pca.acquireTokenPopup({ ...tokenRequest, prompt: "select_account" });
                }

                if (isIdTokenExpiredOrExpiring(userAccount.idToken)) {
                    // Leave idToken unset so the dialog below runs: it is the
                    // only path that does not go through the broker
                    clientLog("[MSAL] Interactive login still returned an expired ID token.");
                    staleTokenPaths.push("popup");
                } else {
                    clientLog("[MSAL] Acquired token interactively.");
                    idToken = userAccount.idToken;
                    tokenSource = tokenSource || "popup";
                }
            } catch (ex) {
                popupError = ex;

                // Fallback to Dialog API for common popup/monitor issues
                const code = (ex as any)?.errorCode || (ex as any)?.error || "";
                const isPopupIssue = ex instanceof BrowserAuthError ||
                    ["popup_window_error", "popup_closed_error", "monitor_window_timeout", "user_cancelled", "interaction_in_progress"].some(c => (code || "").indexOf(c) >= 0);

                if (!isPopupIssue) {
                    // Acquire token interactive failure: consent, conditional
                    // access or a rejected account, not a popup problem, and
                    // the dialog would fail on the same thing
                    popupFailedForGood = true;
                    setInitFailure(
                        StartupErrorCode.INTERACTIVE,
                        phase,
                        "There was an error starting the plugin. Sign-in could not be completed.",
                        serializeError(ex));
                }
            }

            // Either the popup could not open, or it returned a token that had
            // already expired. Both are recovered the same way: the dialog runs
            // its own MSAL instance outside the host broker, so it asks Entra
            // for a token instead of reusing whatever the host is holding.
            if (!idToken && !popupFailedForGood) {
                phase = "acquire-dialog";
                dialogUsedDuringSignIn = true;
                try {
                    // A popup that succeeded and still returned an expired
                    // token means the broker is stuck, so make the dialog ask
                    // for credentials rather than let it reuse its own session
                    const dialogToken = await getTokenWithDialogApi(popupError == null);
                    if (isIdTokenExpiredOrExpiring(dialogToken)) {
                        clientLog("[MSAL] Dialog login returned an expired ID token.");
                        staleTokenPaths.push("dialog");
                    } else {
                        idToken = dialogToken;
                        tokenSource = "dialog";
                    }
                } catch (dialogErr) {
                    clientLog("getTokenWithDialogApi failed: " + (dialogErr?.message || dialogErr));
                    setInitFailure(StartupErrorCode.DIALOG_FALLBACK, phase, defaultInitErrorMessage, {
                        popupError: serializeError(popupError),
                        dialogError: serializeError(dialogErr),
                    }, onReset);
                }
            }
        }

        // Log error if both silent and popup (and dialog) requests failed.
        if (!idToken) {
            if (initError == null) {
                const everyPathStale = staleTokenPaths.length > 0;
                setInitFailure(StartupErrorCode.NO_TOKEN, phase,
                    everyPathStale
                        ? "There was an error starting the plugin. Outlook kept returning a sign-in that had already expired."
                        : "There was an error starting the plugin. Sign-in finished without returning a token.",
                    { staleTokenPaths, forceTokenRefresh, forceInteractive },
                    onReset);
            }
            render(App);
        } else {

            var decoded = decodeJwtExpiration(idToken);
            if (decoded) {
                clientLog("Token received which expires at: " + decoded.expirationDate);
            }
            userIdentityToken = idToken;
            userIdentityTokenSource = tokenSource;
            phase = "save-item";
            saveItem();
        }

    } catch (ex) {
        clientLog("Error onSignIn")
        clientLog(ex)
        failInit(StartupErrorCode.MSAL_INIT, phase, defaultInitErrorMessage, serializeError(ex));
    } finally {
        authInProgress = false;
    }
};



/** FALLBACK FOR NAA */
export type AuthDialogResult = {
    idToken?: string;
    error?: string;
    // Which branch of the dialog produced the result, for diagnostics
    source?: string;
};

var _dialogApiResult: Promise<string> | null = null;

type DialogEventMessage = { message: string; origin: string | undefined };
type DialogEventError = { error: number };
type DialogEventArg = DialogEventMessage | DialogEventError;

/**
 * Signs in through the Office Dialog API, which loads dialog.html in its own
 * webview with its own MSAL instance. That is the only path here that does not
 * go through the Outlook host's nested app auth broker, so it is what recovers
 * a session when the broker keeps serving a token that has already expired.
 *
 * forceFresh skips the dialog's own silent step and asks Entra for credentials,
 * for the case where every cached path has already produced a stale token.
 */
const getTokenWithDialogApi: (forceFresh?: boolean) => Promise<string> = (forceFresh = false) => {
    const dialogUrl = createLocalUrl(forceFresh ? `dialog.html?forceFresh=1` : `dialog.html`);

    _dialogApiResult = new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 30 }, (result) => {
            // A host that refuses to open the dialog (a blocked popup, a
            // dialog already open) reports it here, and result.value is then
            // undefined. Reading it regardless used to surface as an unrelated
            // TypeError.
            if (result.status !== Office.AsyncResultStatus.Succeeded || !result.value) {
                _dialogApiResult = null;
                reject(new Error("Login window could not be opened. " + ((result.error && result.error.message) || "")));
                return;
            }

            result.value.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogEventArg) => {
                const errorArg = arg as DialogEventError;
                if (errorArg.error == 12006) {
                    _dialogApiResult = null;
                    reject("Login window closed");
                }
            });
            result.value.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogEventArg) => {
                const messageArg = arg as DialogEventMessage;
                result.value.close();

                let parsedMessage: AuthDialogResult = null;
                try {
                    parsedMessage = JSON.parse(messageArg.message);
                } catch {
                    // The dialog also posts plain strings ("close"), and a
                    // crashing dialog can post anything at all
                    _dialogApiResult = null;
                    reject("Login window returned an unreadable response");
                    return;
                }

                if (parsedMessage && parsedMessage.error) {
                    reject(parsedMessage.error);
                    _dialogApiResult = null;
                } else if (parsedMessage && parsedMessage.idToken) {
                    resolve(parsedMessage.idToken);
                } else {
                    reject("Dialog returned no token");
                    _dialogApiResult = null;
                }
            });
        });
    });
    return _dialogApiResult;
}
/** END FALLBACK FOR NAA */

// Same bounded backoff EmailTypeService uses for the identical condition
const SAVE_ITEM_RETRY_DELAYS_MS = RETRY_DELAYS_MS;

// Office publishes no timeout for saveAsync, and a callback that never arrives
// would leave the pane on a spinner indefinitely. Saving a draft with
// attachments to Exchange is genuinely slow, so keep this generous: it is a
// backstop for a lost callback, not a performance limit.
const SAVE_ITEM_TIMEOUT_MS = 20000;

let saveItemAttempt = 0;
// Guards against a callback and the timeout both reporting the same attempt,
// and against a late callback arriving after the timeout already gave up
let saveItemSettled = false;
let saveItemTimeoutHandle = null;

const clearSaveItemTimeout = () => {
    if (saveItemTimeoutHandle != null) {
        clearTimeout(saveItemTimeoutHandle);
        saveItemTimeoutHandle = null;
    }
}

/**
 * Saves the draft to obtain its Exchange item id.
 *
 * Interactive sign-in can hold the task pane for tens of seconds, so by the
 * time this runs another Office.js call on the item (Outlook's own autosave,
 * a second pane) may still be in flight, and the host answers 5100. Only that
 * condition is retried, at most SAVE_ITEM_RETRY_DELAYS_MS.length times; every
 * other host error surfaces immediately.
 *
 * A lost callback is reported once, through the timeout, and never retried:
 * unlike 5100 there is no reason to expect a second attempt to fare better.
 */
const saveItem = () => {
    saveItemAttempt = 0;
    clearSaveItemTimeout();
    saveItemSettled = false;
    invokeSaveItem();
}

const invokeSaveItem = () => {
    try {
        const item = Office.context.mailbox.item;
        if (item == null || typeof item.saveAsync !== 'function') {
            // No compose item: the user switched to a read item or closed the
            // draft while sign-in was open
            failInit(StartupErrorCode.SAVE_ITEM, "save-item", saveItemErrorMessage, {
                officeErrorMessage: "No compose item is available to save",
                attempts: saveItemAttempt + 1,
            }, saveItem);
            return;
        }

        saveItemSettled = false;
        clearSaveItemTimeout();
        saveItemTimeoutHandle = setTimeout(() => {
            saveItemTimeoutHandle = null;
            if (saveItemSettled) {
                return;
            }
            saveItemSettled = true;

            clientLog(`saveAsync did not call back within ${SAVE_ITEM_TIMEOUT_MS}ms`);
            failInit(StartupErrorCode.SAVE_ITEM, "save-item", saveItemErrorMessage, {
                officeErrorMessage: `Outlook did not respond within ${SAVE_ITEM_TIMEOUT_MS / 1000} seconds`,
                timedOut: true,
                attempts: saveItemAttempt + 1,
            }, saveItem);
        }, SAVE_ITEM_TIMEOUT_MS);

        item.saveAsync(onAfterSaveItem);
    }
    catch (ex) {
        clearSaveItemTimeout();
        saveItemSettled = true;
        failInit(StartupErrorCode.SAVE_ITEM, "save-item", saveItemErrorMessage, {
            ...serializeError(ex),
            attempts: saveItemAttempt + 1,
        }, saveItem);
    }
}

const onAfterSaveItem = (result: Office.AsyncResult<string>) => {
    if (saveItemSettled) {
        // The timeout already reported this attempt; a late callback must not
        // resurrect a flow the user has been told about
        clientLog("Late saveAsync callback ignored");
        return;
    }
    saveItemSettled = true;
    clearSaveItemTimeout();

    if (result.status != Office.AsyncResultStatus.Succeeded
        && isTransientOfficeError(result)
        && saveItemAttempt < SAVE_ITEM_RETRY_DELAYS_MS.length) {

        const delayMs = SAVE_ITEM_RETRY_DELAYS_MS[saveItemAttempt];
        saveItemAttempt++;
        clientLog(`saveAsync reported a previous call in progress, retry ${saveItemAttempt} in ${delayMs}ms`);
        setTimeout(invokeSaveItem, delayMs);
        return;
    }

    handleErrors("onAfterSaveItem", result, () => {
        exchangeItemID = result.value
        Debugger.appendLog("exchangeItemID:" + exchangeItemID);
        linkToApi();
    }, { attempts: saveItemAttempt + 1 }, saveItem);
}

// A failing link call is worth repeating: the identity token is already valid
// and the draft is already saved, so a retry costs one request and never
// re-enters sign-in, which keeps it clear of the auth loop breaker.
const LINK_RETRY_DELAYS_MS = [2000, 5000];

let linkAttempt = 0;

/**
 * Repeats the link call after a delay. Bounded on purpose: during a real
 * outage every client would otherwise sit and poll the service that is
 * already struggling.
 *
 * Returns false when the retries are used up, so the caller reports the
 * failure instead.
 */
const scheduleLinkRetry = (): boolean => {
    if (linkAttempt >= LINK_RETRY_DELAYS_MS.length) {
        return false;
    }

    const delayMs = LINK_RETRY_DELAYS_MS[linkAttempt];
    linkAttempt++;

    clientLog(`Link failed, retry ${linkAttempt} of ${LINK_RETRY_DELAYS_MS.length} in ${delayMs}ms`);

    initRetryMessage = `Reconnecting... (attempt ${linkAttempt + 1} of ${LINK_RETRY_DELAYS_MS.length + 1})`;
    render(App);

    setTimeout(invokeLinkToApi, delayMs);
    return true;
}

const linkToApi = () => {
    linkAttempt = 0;
    invokeLinkToApi();
}

const invokeLinkToApi = () => {
    try {
        // Sign-in, saveAsync and its retries all run between acquiring the
        // token and this call, so the token can have aged out in between.
        // Sending it anyway costs a round trip and, because the server answers
        // ReAuth, one of the two attempts the loop breaker allows. Handing it
        // to onAfterServerLink instead enters the same recovery ladder the
        // server's own rejection would, without spending the attempt.
        if (isIdTokenExpiredOrExpiring(userIdentityToken)) {
            clientLog("Identity token expired before the link call; re-authenticating instead of sending it.");
            onAfterServerLink({
                success: false,
                reAuth: true,
                reason: "The sign-in token expired before it could be used.",
                expiredBeforeSend: true,
            });
            return;
        }

        // Ready for deprecation
        var restUrl = "";
        //try {
        //    restUrl = Office.context.mailbox.restUrl;
        //} catch { }

        const pluginEmail = Office?.context?.mailbox?.userProfile?.emailAddress;

        apiLink(
            window.location.href,
            userIdentityToken,
            userAccessToken,
            restUrl,
            Office.context.mailbox.diagnostics.hostName,
            pluginEmail
        )
            .then(onAfterServerLink)
            .catch((ex) => {
                // The request itself failed (network, 5xx, CORS); a failure
                // the server could describe comes back as a LinkResult.
                // Always worth repeating, since nothing here needs the user.
                if (scheduleLinkRetry()) {
                    return;
                }
                failInit(StartupErrorCode.LINK_REQUEST, "link-request", defaultInitErrorMessage, serializeError(ex), linkToApi);
            })
    }
    catch (ex) {
        failInit(StartupErrorCode.LINK_REQUEST, "link-prepare", defaultInitErrorMessage, serializeError(ex), linkToApi);
    }
}

var retriedAuth = false;
var previousToken = "";
var previousTokenSource = null;

/**
 * What the client knows about the token the server just rejected. Enough to
 * tell a stale host broker (a token the client could see was expired, or the
 * identical token handed back twice) from a genuine sign-in failure, without
 * ever putting the token itself in a log.
 */
const describeTokenAttempt = () => ({
    tokenSource: userIdentityTokenSource,
    currentTokenExpiry: describeTokenExpiry(userIdentityToken),
    previousTokenSource,
    previousTokenExpiry: describeTokenExpiry(previousToken),
    // True when the re-acquire returned the very same JWT, which is the
    // signature of a broker that is ignoring forceRefresh
    sameTokenAsPrevious: !!userIdentityToken && userIdentityToken === previousToken,
    retriedAuth,
    dialogUsedDuringSignIn,
    forceFreshDialogUsed,
});
const onAfterServerLink = async (linkResult) => {

    initRetryMessage = null;

    if (!linkResult.reAuth) {
        // The server accepted the identity token (any remaining problem is not
        // an auth loop), so reset the loop-breaker state for future episodes
        authAttemptCount = 0;
        lastAuthAttemptAt = 0;
        retriedAuth = false;
        forceFreshDialogUsed = false;
        dialogUsedDuringSignIn = false;

        // Only a token the server accepted proves the sign-out is over. Clearing
        // this as soon as any token arrived lost the account picker for the
        // retry, which is exactly when the user needs it.
        clearForceInteractiveLogin();
    }

    if (linkResult.success === true) {
        clientLog("onAfterServerLink: OK");
        apiLinked = true;
    }
    else {
        if (linkResult.completeSetup) {
            clientLog("Account problem in onAfterServerLink: " + linkResult.reason);
            initMustSetup = true;
            apiLinked = true;
        }
        else if (linkResult.reAuth) {

            if (!retriedAuth) {
                previousToken = userIdentityToken;
                previousTokenSource = userIdentityTokenSource;
                clientLog("Invalid token in onAfterServerLink: " + linkResult.reason);
                retriedAuth = true;

                clearAllLocalState();

                // Throttle re-auth retry if we've already tried too often in the last minute
                if (tooManyAuthAttemptsNow()) {
                    breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.", linkResult);
                } else {
                    // The server just rejected the cached token, so the retry
                    // must bypass the MSAL/broker cache
                    onSignIn(true);
                }
            } else {

                clientLog("Invalid token twice in onAfterServerLink: " + linkResult.reason);
                // Set without rendering: the dialog retry below may still
                // succeed, and an error flash before the login window is worse
                // than none
                setInitFailure(
                    StartupErrorCode.REAUTH_FAILED,
                    "reauth",
                    "There was a problem signing-in to your account. Re-auth was attempted but failed. " + linkResult.reason,
                    {
                        linkResult,
                        ...describeTokenAttempt(),
                    },
                    onReset);

                // Deliberately not gated on tooManyAuthAttemptsNow(): reaching
                // this point always costs exactly the two attempts the throttle
                // allows (the first sign-in, then the automatic re-auth), so
                // gating it made the dialog unreachable and turned every stale
                // token into MM-10. The dialog needs the user to click, so one
                // attempt per episode cannot spin.
                if (forceFreshDialogUsed) {
                    breakAuthLoop("We detected repeated sign-in attempts. Please close and reopen the add-in or press Ctrl/Cmd+R to refresh. If it persists, disable popup blockers and try again.", linkResult);
                } else {
                    forceFreshDialogUsed = true;
                    try {
                        // Every cached path has now produced a token the server
                        // rejected, so ask for credentials rather than let the
                        // dialog reuse a session that may be just as stale
                        const dialogToken = await getTokenWithDialogApi(true);
                        if (!dialogToken) {
                            clientLog("Dialog returned no token after re-auth.");
                        } else if (isIdTokenExpiredOrExpiring(dialogToken)) {
                            // Nothing left to try: a fresh interactive login
                            // handed back an expired token
                            clientLog("Dialog returned an expired ID token after re-auth.");
                        } else {
                            userIdentityToken = dialogToken;
                            userIdentityTokenSource = "dialog-force-fresh";
                            initEx = null;
                            initError = null;
                            initHelpLink = null;
                            initRetryAction = null;
                            // Use the fresh dialog token directly; restarting
                            // onSignIn would discard it and re-read the stale
                            // cached token
                            saveItem();
                        }
                    } catch (ex) {
                        clientLog("getTokenWithDialogApi failed: " + (ex?.message || ex));
                    }
                }
            }

        }
        else {
            clientLog("Error in onAfterServerLink: " + linkResult.reason);

            // The server marked this as worth repeating on its own (it could
            // not reach Microsoft, say), so try again before showing anything
            if (linkResult.retryAutomatically && scheduleLinkRetry()) {
                return;
            }

            // A failure the user has to act on first (deleting the bounce
            // folder) gets the button but no automatic repeat
            const canRetry = linkResult.retryAutomatically || linkResult.retryManually;

            // The server described the failure, so its reason stays the
            // headline; the code only tells support which branch it came from
            setInitFailure(
                StartupErrorCode.LINK_REJECTED,
                "link-rejected",
                "There was a problem signing-in to your account. " + linkResult.reason,
                { linkResult },
                canRetry ? linkToApi : undefined);
        }
    }

    render(App);
}

try {
    var locale = (window.navigator as any).userLanguage || window.navigator.language;
    moment.locale(locale)

    initializeIcons();

    initTheme();

    var utm_source = getUrlParameter("utm_source");
    var utm_medium = getUrlParameter("utm_medium");
    var utm_content = getUrlParameter("utm_content");
    var utm_campaign = getUrlParameter("utm_campaign");
    var utm_term = getUrlParameter("utm_term");
    var referer_qs = getUrlParameter("referer");
    var referer_doc = (document.referrer && encodeURIComponent(document.referrer));

    if (utm_source && utm_source.length > 0) setCookie("utm_source", utm_source);
    if (utm_medium && utm_medium.length > 0) setCookie("utm_medium", utm_medium);
    if (utm_content && utm_content.length > 0) setCookie("utm_content", utm_content);
    if (utm_campaign && utm_campaign.length > 0) setCookie("utm_campaign", utm_campaign);
    if (utm_term && utm_term.length > 0) setCookie("utm_term", utm_term);
    if (referer_qs && referer_qs.length > 0) {
        setCookie("referer", referer_qs);
    } else if ((getCookie('referer') || '').length == 0 && referer_doc && referer_doc.length > 0) {
        setCookie("referer", referer_doc);
    }

    /* Render application after Office initializes */
    Office.initialize = () => { };

    Office.onReady(function (info) {
        isOfficeInitialized = true;
        onSignIn();
    });


    /* Initial render showing a progress bar */
    render(App);

    //if ((module as any).hot) {
    //    (module as any).hot.accept('./components/App', () => {
    //        const NextApp = require('./components/App').default;
    //        render(NextApp);
    //    });
    //}

} catch (ex) {
    var body = document.querySelector("body");
    var errContent = "<div style='padding: 20px; text-align: center; display: flex; flex-direction: column; justify-content: center; align-items: center;'><b>Error:</b><span>" + ex + "</span></div>";
    if (body) {
        body.innerHTML = errContent;
    } else {
        document.write(errContent)
    }
}